/**
 * MarketingFooter.js
 * Shared footer component for public/marketing pages
 * Used by MissionVision, PrivacyPolicy, TermsOfService (light variant)
 * and LandingSky (dark variant)
 *
 * The copyright string and the set of legal/about links live here, in one place,
 * for every public page. Variants change how they are presented, never what
 * they say.
 *
 * @param {'light'|'dark'} [variant='light']
 *   'light' — the original single centred line, for white pages. Its markup and
 *             classes are unchanged; it renders byte-for-byte as before.
 *   'dark'  — for the full-bleed dark landing sky: links left, copyright right,
 *             smaller and dimmer. Matches
 *             docs/design/landing-sky/landing-sky-reference.html.
 */

import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './MarketingFooter.css';

const MarketingFooter = ({ variant = 'light' }) => {
  // Called unconditionally — hooks cannot sit behind a branch. Only the light
  // variant uses it.
  const navigate = useNavigate();

  if (variant === 'dark') {
    return (
      <footer className="marketing-footer marketing-footer-dark">
        <nav className="marketing-footer-dark-links" aria-label="About and legal">
          {/*
            BLOG — placeholder, deliberately not rendered.

            The approved reference lists Blog first in this row, but no blog
            exists yet, so there is nothing to link to. When it does, add it
            here, first in the order, as a sibling <Link>:

                <Link to="/blog">Blog</Link>

            Left as a comment rather than a hidden element so there is no dead
            anchor in the accessibility tree.
          */}
          <Link to="/mission-vision">Mission &amp; Vision</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
        </nav>
        {/*
          The same notice the light variant carries. Kept as literal text in both
          branches rather than hoisted to a constant: it is a legal string, and a
          reader checking it should find it where it renders.
        */}
        <div className="marketing-footer-dark-copyright">
          © 2025–2026 Constellation KG LLC. All rights reserved.
        </div>
      </footer>
    );
  }

  // ---------------------------------------------------------------------------
  // Light variant — unchanged. <button> + navigate rather than <Link> is the
  // original idiom here; the dark variant uses <Link> because a public marketing
  // footer should offer real anchors (open-in-new-tab, copy link). Deliberate
  // asymmetry, not drift: touching this markup would change four live pages.
  // ---------------------------------------------------------------------------
  return (
    <footer className="marketing-footer">
      <p>
        <button onClick={() => navigate('/mission-vision')} className="marketing-footer-link">
          Mission &amp; Vision
        </button>
        {' • '}
        © 2025–2026 Constellation KG LLC. All rights reserved.
        {' • '}
        <button onClick={() => navigate('/privacy')} className="marketing-footer-link">
          Privacy Policy
        </button>
        {' • '}
        <button onClick={() => navigate('/terms')} className="marketing-footer-link">
          Terms of Service
        </button>
      </p>
    </footer>
  );
};

export default MarketingFooter;
